import { useEffect } from "react";

import { useSearchParams } from "react-router";

import {
  GetCoachingProgramsQuery,
  useGetAllCoachProductsQuery,
} from "@/graphql";
import { Specialty } from "@/types/products";

type Program = GetCoachingProgramsQuery["coachingPrograms"]["programs"][number];
type Product = Program["sessions"][number]["product"];
type CoachProduct = Program["coaches"][number]["coachProduct"];

interface UseProductsProps {
  coachId: string;
  selectedClientId: string | null;
  selectedProduct: Product | null;
  selectedWorkspaceId: string | null;
  showPrograms: boolean;
  onSelectProduct: (product: Product, coachProduct: CoachProduct) => void;
  onSelectSpecialtyId: (specialtyId: string) => void;
  skip: boolean;
  onSetWarningMessage: (message: string) => void;
}

interface UseProductsReturn {
  specialties: Specialty[];
  coachProducts: CoachProduct[];
  loading: boolean;
}

export function useProducts({
  coachId,
  selectedClientId,
  selectedProduct,
  showPrograms,
  selectedWorkspaceId,
  onSelectProduct,
  onSelectSpecialtyId,
  skip,
  onSetWarningMessage,
}: UseProductsProps): UseProductsReturn {
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId");
  const specialtyId = searchParams.get("specialtyId");
  const componentId = searchParams.get("programComponentId");
  const productSlug = searchParams.get("productSlug");

  const { data, loading } = useGetAllCoachProductsQuery({
    variables: {
      coachId,
      forBooking: true,
      clientId: selectedClientId,
      workspaceId: selectedWorkspaceId,
    },
    skip: skip || !!componentId,
  });

  const specialties = data?.productsByCoachId.specialties || [];
  const coachProducts = data?.productsByCoachId.coachProducts || [];

  useEffect(() => {
    if (specialties.length === 0) return;
    if (selectedProduct) return;
    if (componentId) return;

    // console.table(
    //   specialties.map((s) => ({
    //     Specialty: s.name,
    //     ID: s.specialtyId,
    //     Products: s.coachProducts.length,
    //   })),
    // );

    const introProduct = specialties.find((s) =>
      s.products.some((p) => p.introProduct),
    );

    if (productId || productSlug) {
      // Scenario 1: Find product across all specialties and set its specialty
      for (const specialty of specialties) {
        let product = specialty.products.find((p) => p.slug === productSlug);
        if (!product) {
          product = specialty.products.find((p) => p.id === productId);
        }
        const coachProduct = coachProducts.find(
          (cp) => cp.productId === product?.id,
        );
        if (product && coachProduct) {
          onSelectSpecialtyId(specialty.specialtyId);
          onSelectProduct(product, coachProduct);
          return;
        }
      }
      if (introProduct && !showPrograms) {
        // case when we come from a product page logged out and we need to set an intro session
        onSetWarningMessage(
          "Please set up an intro session first to work with this coach.",
        );
      }
    }

    if (specialtyId) {
      // Scenario 2: Find specialty by ID and set its first product
      const specialty = specialties.find((s) => s.specialtyId === specialtyId);
      if (specialty && specialty.products.length > 0) {
        onSelectSpecialtyId(specialty.specialtyId);
        const coachProduct = coachProducts.find(
          (cp) => cp.productId === specialty.products[0].id,
        );
        if (coachProduct) {
          onSelectProduct(specialty.products[0], coachProduct);
        }
        return;
      }
    }

    // Scenario 3: Fallback - first specialty and its first product (only if nothing selected)
    if (specialties[0]?.products?.length > 0) {
      onSelectSpecialtyId(specialties[0].specialtyId);
      const coachProduct = coachProducts.find(
        (cp) => cp.productId === specialties[0].products[0].id,
      );
      if (coachProduct) {
        onSelectProduct(specialties[0].products[0], coachProduct);
      }
    }
  }, [
    specialties,
    productId,
    specialtyId,
    selectedProduct,
    selectedClientId,
    selectedWorkspaceId,
  ]);

  return {
    specialties,
    coachProducts,
    loading,
  };
}
